import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
    Menu,
    Chip,
    Box,
    Divider,
    Stack
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {
    CreateFilter,
    Filter,
} from '../../../Common';
import FilterMenuItem from './FilterMenuItem';
import { TranslateW } from '../../../../Utils/Languages/Translate';
import { trakrefFontFamily, trakrefFontFamilySemiBold, isMobile } from '../../../../utils';
import UserInfo from '../../../../UserInfo';
import { trakrefBlue, Grey3 } from '../../../../color_utils';
import '../Filters.css';

const FilterList = ({
    tableURL,
    filters,
    filterValues,
    appliedFilters,
    setAppliedFilters,
    selectedFilter,
    setSelectedFilter,
    handleClearFilters,
    setClearFilters,
    filterProps,
}) => {
    const defaultFilterText = TranslateW("commonFilters.add-filter");
    const filterBoxWidth = selectedFilter && selectedFilter.type === "date" ? 400 : 300;
    const [filterListAnchor, setFilterListAnchor] = useState(null);
    const [filterOptionAnchor, setFilterOptionAnchor] = useState(null);
    const [filterText, setFilterText] = useState(defaultFilterText);

    const chipStyle = {
        '&.MuiChip-root': { borderRadius: 0, border: 'none' },
        '&.MuiChip-label': { fontFamily: trakrefFontFamilySemiBold },
    };

    const menuStyle = {  
        width: filterBoxWidth,
        height: 'auto',
        maxHeight: '50vh',
    };

    const addFilter = (newFilter) => {
        setFilterText(newFilter.name);
        setSelectedFilter(newFilter);
        setFilterListAnchor(null);

        if (newFilter.type === 'today') {
            const today = moment().format('MM/DD/YYYY');
            return applyFilter(`${today}-${today}`, newFilter);
        }
        setFilterOptionAnchor(filterListAnchor);
    };

    const closeFilter = (filter) => {
        let tempArray = JSON.parse(sessionStorage.getItem(tableURL + "filters" + UserInfo.instanceId));

        if (tempArray === null) {
            tempArray = [...appliedFilters];
        }

        appliedFilters.forEach((fil, index) => {
            if (fil.property === filter.property && fil.value === filter.value) {
                tempArray.splice(index, 1);
            }
        });

        sessionStorage.setItem(tableURL + "filters" + UserInfo.instanceId, JSON.stringify(tempArray));

        setAppliedFilters(tempArray);
        setClearFilters(tempArray.length > 0);
    };

    // passedFilterObject will only ever be used when filtering..
    // ..a date column for today's date
    const applyFilter = (value, passedFilterObject = {}) => {
        setFilterListAnchor(null);
        setFilterOptionAnchor(null);
        setFilterText(defaultFilterText);

        let filter = { value };

        if (!selectedFilter) {
            filter = {
                ...filter,
                property: passedFilterObject.property,
                name: passedFilterObject.name,
                type: passedFilterObject.type,
            };
        } else {
            filter = {
                ...filter,
                property: selectedFilter.property,
                name: selectedFilter.name,
                type: selectedFilter.type,
            };
        }

        let tempArray = JSON.parse(sessionStorage.getItem(tableURL + "filters" + UserInfo.instanceId));

        if (value && value !== '') {

            if (tempArray === null) {
                tempArray = [...appliedFilters];
            }

            tempArray.push(filter);
            sessionStorage.setItem(tableURL + "filters" + UserInfo.instanceId, JSON.stringify(tempArray));
            setAppliedFilters(tempArray);

            setClearFilters(tempArray.length > 0);
        }

        setSelectedFilter(null);
    };
    
    const Menus = () => (
        <>
            <Menu
                anchorEl={filterListAnchor}
                open={Boolean(filterListAnchor)}
                onClose={() => setFilterListAnchor(null)}
                slotProps={{
                    paper: {
                        style: menuStyle
                    }
                }}
            >

                {filters.map((filter) => (
                    <FilterMenuItem
                        filterItem={filter}
                        addFilter={addFilter}
                    />
                ))}
            </Menu>

            <Menu
                anchorEl={filterOptionAnchor}
                open={Boolean(filterOptionAnchor)}
                slotProps={{
                    paper: {
                        style: {
                            ...menuStyle,
                            overflow: 'visible'
                        }
                    }
                }}
                onClose={() => {
                    setFilterOptionAnchor(null);
                    setFilterText(defaultFilterText);
                }}
            >
                {(selectedFilter !== null && selectedFilter.type !== 'today') && (
                    <Box sx={{ p: 2 }}>
                        <CreateFilter
                            property={selectedFilter.property}
                            type={selectedFilter.type}
                            applyFunction={applyFilter}
                            filterValues={filterValues.filter(f => f.value && f.property === selectedFilter.property)}
                            name={selectedFilter.name}
                            filterProps={filterProps}
                            disableDatePickerPortal={true}
                        />
                    </Box>
                )}
            </Menu>
        </>
    );
    
    const ClearFilters = () => (
        <>
            <Divider
                flexItem
                orientation="vertical"
            />
            <Chip
                label={TranslateW("commonFilters.clear-filters")}
                onClick={handleClearFilters}
                variant="outlined"
                sx={{
                    ...chipStyle,
                    fontWeight: 600,
                    fontSize: '.75rem',
                    color: Grey3,
                    letterSpacing: '.88px',
                    fontFamily: trakrefFontFamily,
                }}
            />
        </>
    );
    
    if (isMobile) {
        return (
            <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
            >
                <Chip
                    label={filterText}
                    className='mainAnchor'
                    icon={filterText === defaultFilterText ? <AddIcon /> : null}
                    onClick={e => setFilterListAnchor(e.currentTarget)}
                    variant="outlined"
                    sx={{
                        ...chipStyle,
                        fontWeight: 600,
                        fontSize: '.75rem',
                        letterSpacing: '.88px',
                        fontFamily: trakrefFontFamily,
                        '& .MuiChip-icon': {
                            color: trakrefBlue,
                            fontSize: '.85rem',
                            marginRight: '-10px'
                        }
                    }}
                />

                {appliedFilters.length > 0 && (
                    <ClearFilters />
                )}

                {appliedFilters.map(filter => (
                    <Filter
                        property={filter.property}
                        name={filter.name}
                        value={filter.value}
                        closeFilter={closeFilter}
                        todayFilter={filter.type === 'today'}
                    />
                ))}

                <Menus />
            </Stack>
        );
    }

    return (
        <div className="filter-row row no-margin">

            {appliedFilters.map(filter => (
                <Filter
                    property={filter.property}
                    name={filter.name}
                    value={filter.value}
                    closeFilter={closeFilter}
                    todayFilter={filter.type === 'today'}
                />
            ))}

            <Chip
                label={filterText}
                className='mainAnchor'
                icon={filterText === defaultFilterText ? <AddIcon /> : null}
                onClick={e => setFilterListAnchor(e.currentTarget)}
                variant="outlined"
                sx={{
                    ...chipStyle,
                    fontWeight: 600,
                    fontSize: '.75rem',
                    letterSpacing: '.88px',
                    fontFamily: trakrefFontFamily,
                    '& .MuiChip-icon': {
                        color: trakrefBlue,
                        fontSize: '.85rem',
                        marginRight: '-10px'
                    }
                }}
            />

            {appliedFilters.length > 0 && (
                <ClearFilters />
            )}

            <Menus />
        </div>
    );
};

FilterList.propTypes = {
    tableURL: PropTypes.string,
    filters: PropTypes.array.isRequired,
    filterValues: PropTypes.array.isRequired,
    appliedFilters: PropTypes.object.isRequired,
    setAppliedFilters: PropTypes.func.isRequired,
    selectedFilter: PropTypes.object.isRequired,
    setSelectedFilter: PropTypes.func.isRequired,
    handleClearFilters: PropTypes.func.isRequired,
    filterProps: PropTypes.object,
};

FilterList.defaultProps = {
    tableURL: '',
    filterProps: {},
};

export default FilterList;
